<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="deadlineEntriesWithStatus"
      no-data-text="Keine Fristeneinträge"
      sort-by="dueDate"
      :group-by.sync="groupBySelector.value"
      :search="search"
    >
      <template v-slot:top>
        <slot name="top">
          <v-card flat>
            <v-card-title v-if="!hideTitle" class="body-2 v-card__subtitle"
              ><v-icon left small class="mr-2">mdi-timer-sand</v-icon
              >{{ title }}
              <v-spacer></v-spacer>
            </v-card-title>
            <v-card-subtitle>
              <v-row>
                <v-col cols="12" sm="5" md="3">
                  <v-text-field
                    v-model="search"
                    outlined
                    append-icon="mdi-magnify"
                    label="Suchen"
                    hide-details="auto"
                    clearable
                    dense
                  ></v-text-field>
                </v-col>
                <v-col v-if="allowGrouping" class="d-flex justify-end">
                  <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        outlined
                        text
                        v-bind="attrs"
                        v-on="on"
                        class="mr-2"
                      >
                        <span v-if="groupBySelector.value == null"
                          >Gruppieren</span
                        >
                        <span v-else>
                          Gruppieren nach
                          <strong class="hidden-sm-and-down">{{
                            groupBySelector.text
                          }}</strong>
                          <v-icon small class="hidden-md-and-up">{{
                            groupBySelector.icon
                          }}</v-icon>
                        </span>
                        <v-icon right>mdi-menu-down</v-icon>
                      </v-btn>
                    </template>
                    <v-list nav>
                      <v-list-item-group v-model="groupBySelector" mandatory>
                        <v-list-item
                          link
                          dense
                          :value="{
                            value: null,
                          }"
                        >
                          <v-list-item-icon>
                            <v-icon dense>mdi-close</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title
                            >Gruppierung aufheben</v-list-item-title
                          >
                        </v-list-item>
                        <v-list-item
                          v-if="allowGroupingByStatus"
                          link
                          dense
                          :value="{
                            text: 'Status',
                            value: 'groupByStatus',
                            icon: 'mdi-clock',
                          }"
                        >
                          <v-list-item-icon>
                            <v-icon dense>mdi-list-status</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Status</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                          v-if="allowGroupingByDeadline"
                          link
                          dense
                          :value="{
                            text: 'Frist',
                            value: 'groupByDeadline',
                            icon: 'mdi-clock',
                          }"
                        >
                          <v-list-item-icon>
                            <v-icon dense>mdi-clock</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Frist</v-list-item-title>
                        </v-list-item>
                        <v-list-item
                          v-if="allowGroupingByPerson"
                          link
                          dense
                          :value="{
                            text: 'Person',
                            value: 'groupByPerson',
                            icon: 'mdi-account',
                          }"
                        >
                          <v-list-item-icon>
                            <v-icon dense>mdi-account</v-icon>
                          </v-list-item-icon>
                          <v-list-item-title>Person</v-list-item-title>
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                  </v-menu>

                  <v-btn v-if="allowExport" outlined text disabled>
                    <v-icon left>mdi-file-export</v-icon>
                    Exportieren
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-subtitle>
          </v-card>
        </slot>
      </template>
      <template v-slot:[`group.header`]="{ items, isOpen, toggle }">
        <th :colspan="headers.length + 1">
          <v-icon @click="toggle" class="mr-2"
            >{{ isOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>

          <span v-if="groupBySelector.value == 'groupByPerson'"
            >{{ items[0].user.displayName }}
          </span>
          <span v-else-if="groupBySelector.value == 'groupByDeadline'">{{
            items[0].deadline.title
          }}</span>
          ({{ items.length }})
        </th>
      </template>
      <template v-slot:[`item.labels`]="{ item }">
        <v-chip
          small
          outlined
          v-for="(label, index) in item.labels"
          :key="index"
          class="py-0 mb-0 mt-1 mr-1"
        >
          {{ label }}</v-chip
        >
      </template>
      <template v-slot:[`item.personnelNumber`]="{ item }">
        <pre>{{ item.user.personnelNumber }}</pre>
      </template>
      <template v-slot:[`item.dueDate`]="{ item }">
        <pre v-if="item.dueDate">{{ timestampConverter(item.dueDate) }}</pre>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <deadline-entry-status-chip
          :status="item.status"
          small
        ></deadline-entry-status-chip>
      </template>
      <template v-slot:[`item.scheduledRenewal`]="{ item }">
        <span v-if="item.scheduledRenewal">
          <strong>{{
            timestampConverterWithTime(item.scheduledRenewal.timestamp)
          }}</strong
          ><br />
          {{ item.scheduledRenewal.location }}, {{ item.scheduledRenewal.note }}
        </span>
      </template>
      <template v-slot:[`item.pendingUpdate`]="{ item }">
        <pre v-if="item.pendingUpdate?.newDueDate">{{
          timestampConverter(item.pendingUpdate.newDueDate)
        }}</pre>

        <span v-if="item.pendingUpdate?.newLabels.length">
          <v-chip
            x-small
            outlined
            v-for="(label, index) in item.pendingUpdate.newLabels"
            :key="index"
            class="mr-1"
          >
            {{ label }}</v-chip
          ></span
        >
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn icon @click="toggleDeadlineEntryUpdateDialog(item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon @click="toggleHistoryDialog(item)">
          <v-icon>mdi-history</v-icon>
        </v-btn>
        <v-btn icon @click="toggleScheduleRenewalDialog(item)">
          <v-icon>mdi-calendar-plus</v-icon>
        </v-btn>
        <v-btn icon disabled>
          <v-icon disabled>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>
    <DeadlineEntryUpdateDialog
      v-if="helpers.deadlineEntryUpdateDialog.model"
      @close="toggleDeadlineEntryUpdateDialog()"
      :deadlineEntry="helpers.deadlineEntryUpdateDialog.item"
      hidden
    ></DeadlineEntryUpdateDialog>
    <DeadlineEntryRenewalDialog
      v-if="helpers.scheduleRenewalDialog.model"
      @close="toggleScheduleRenewalDialog()"
      :deadlineEntry="helpers.scheduleRenewalDialog.item"
      hidden
    ></DeadlineEntryRenewalDialog>
    <CustomDialog
      v-if="helpers.historyDialog.model"
      @close="toggleHistoryDialog()"
      title="Historie für Fristeneintrag"
      :loading="helpers.historyDialog.loading"
      :errorMessage="helpers.historyDialog.errorMessage"
      hideActionBtn
      showCloseBtn
      fullscreenOnMobile
      width="m"
    >
      <template v-slot:content>
        <div v-if="helpers.historyDialog.item" class="mb-4">
          <v-card outlined>
            <v-card-title class="py-2 font-weight-medium subtitle-2">
              Aktueller Fristeneintrag
            </v-card-title>
            <v-divider></v-divider>
            <deadline-entry-list-item
              :deadlineEntry="helpers.historyDialog.item"
              hideActions
            ></deadline-entry-list-item>
          </v-card>
        </div>

        <LogsCommentsTimeline
          :timeline-entries="historyData"
          :action-text-map="actionTextMap"
          :color-map="colorMap"
          :icon-map="iconMap"
          :timestamp-converter="timestampConverterWithTime"
        >
        </LogsCommentsTimeline>
      </template>
    </CustomDialog>
  </div>
</template>

<script>
import { db } from "@/firebase";
import DeadlineEntryListItem from "@/components/member-management/deadlines/DeadlineEntryListItem.vue";
import DeadlineEntryUpdateDialog from "@/components/member-management/deadlines/DeadlineEntryUpdateDialog.vue";
import DeadlineEntryRenewalDialog from "@/components/member-management/deadlines/DeadlineEntryRenewalDialog.vue";
import CustomDialog from "@/components/_systemwide/CustomDialog.vue";
import LogsCommentsTimeline from "@/components/_systemwide/LogsCommentsTimeline.vue";

export default {
  name: "personnel-records-deadlines-data-table",
  props: {
    hideTitle: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Fristeneinträge",
    },
    deadlines: {
      type: Array,
      required: true,
    },
    showNames: {
      type: Boolean,
      default: false,
    },
    showDeadline: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showDocuments: {
      type: Boolean,
      default: false,
    },
    showHistory: {
      type: Boolean,
      default: false,
    },
    allowGrouping: {
      type: Boolean,
      default: false,
    },
    allowGroupingByStatus: {
      type: Boolean,
      default: false,
    },
    allowGroupingByPerson: {
      type: Boolean,
      default: false,
    },
    allowGroupingByDeadline: {
      type: Boolean,
      default: false,
    },
    allowExport: {
      type: Boolean,
      default: false,
    },
    organizationId: {
      type: String,
      default: null,
    },
  },
  components: {
    CustomDialog,
    DeadlineEntryUpdateDialog,
    DeadlineEntryRenewalDialog,
    LogsCommentsTimeline,
    DeadlineEntryListItem,
  },
  data() {
    return {
      groupBySelector: { value: null },
      search: "",
      historyData: [],
      helpers: {
        historyDialog: {
          model: false,
          item: null,
          loading: false,
          errorMessage: "",
        },
        deadlineEntryUpdateDialog: {
          model: false,
          item: null,
        },
        scheduleRenewalDialog: {
          model: false,
          item: null,
        },
      },
    };
  },
  computed: {
    headers() {
      let headers = [
        { text: "Ablaufdatum", value: "dueDate", divider: true },
        { text: "Status", value: "status", divider: true },
        { text: "Labels", value: "labels", divider: true },
        { text: "Bemerkungen", value: "note", divider: true },
        {
          text: "Ausstehende Aktualisierung",
          value: "pendingUpdate",
          divider: true,
        },
        {
          text: "Geplante Verlängerung",
          value: "scheduledRenewal",
          divider: true,
        },
      ];

      if (this.showDocuments) {
        headers.splice(4, 0, {
          text: "Dokumente",
          value: "documents",
          divider: true,
        });
      }
      if (
        !(
          this.showDeadline &&
          this.allowGrouping &&
          this.groupBySelector.value === "groupByDeadline"
        )
      ) {
        headers.unshift({
          text: "Frist",
          value: "deadline.shortTitle",
          divider: true,
        });
      }
      if (this.showNames) {
        headers.unshift({
          text: "Nachname",
          value: "user.lastName",
          divider: true,
        });
        headers.unshift({
          text: "Vorname",
          value: "user.firstName",
          divider: true,
        });
        headers.unshift({
          text: "Personalnummer",
          value: "user.personnelNumber",
          divider: true,
        });
      }
      if (this.showActions) {
        headers.push({
          text: "Aktionen",
          value: "actions",
          sortable: false,
        });
      }
      return headers;
    },
    deadlineEntriesWithStatus() {
      return this.deadlines.map((entry) => {
        const deadlineTimestamp = entry.dueDate;
        if (!deadlineTimestamp || !deadlineTimestamp.toDate) {
          return { ...entry, status: "unknown" };
        }
        const currentDate = new Date();
        const deadlineDate = deadlineTimestamp.toDate();
        const status = deadlineDate < currentDate ? "expired" : "valid";
        return {
          ...entry,
          status,
          groupByDeadline: entry.deadline.id,
          groupByPerson: entry.user.membershipId,
          groupByStatus: status,
        };
      });
    },
    actionTextMap() {
      return {
        deadlineEntryCreated: "hat den Fristeneintrag erstellt.",
        deadlineEntryUpdated: "hat den Fristeneintrag aktualisiert.",
        renewed: "hat den Fristeneintrag verlängert.",
        scheduledRenewal: "hat eine Verlängerung geplant.",
        cancelledRenewal: "hat eine geplante Verlängerung storniert.",
        comment: "hat einen Kommentar hinzugefügt:",
      };
    },
    colorMap() {
      return {
        created: "primary",
        updated: "blue",
        renewed: "green",
        scheduled: "orange",
        cancelled: "red",
        expired: "red",
        comment: "grey",
      };
    },
    iconMap() {
      return {
        created: "mdi-plus",
        updated: "mdi-pencil",
        renewed: "mdi-calendar-check",
        scheduled: "mdi-calendar-plus",
        cancelled: "mdi-calendar-remove",
        expired: "mdi-timer-sand-empty",
        comment: "mdi-comment-text",
      };
    },
  },
  methods: {
    timestampConverter(timestamp) {
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },
    timestampConverterWithTime(timestamp) {
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      };
      return timestamp.toDate().toLocaleDateString("de-DE", options);
    },
    toggleDeadlineEntryUpdateDialog(item) {
      if (item) {
        this.helpers.deadlineEntryUpdateDialog.item = item;
      } else {
        this.helpers.deadlineEntryUpdateDialog.item = null;
      }
      this.helpers.deadlineEntryUpdateDialog.model =
        !this.helpers.deadlineEntryUpdateDialog.model;
    },
    toggleScheduleRenewalDialog(item) {
      if (item) {
        this.helpers.scheduleRenewalDialog.item = item;
      } else {
        this.helpers.scheduleRenewalDialog.item = null;
      }
      this.helpers.scheduleRenewalDialog.model =
        !this.helpers.scheduleRenewalDialog.model;
    },
    async loadHistoryData(deadlineEntry) {
      if (!deadlineEntry?.meta?.id) {
        this.helpers.historyDialog.errorMessage =
          "Keine gültige Fristeneintrag-ID gefunden.";
        return;
      }

      this.helpers.historyDialog.loading = true;
      this.helpers.historyDialog.errorMessage = "";
      this.historyData = [];

      try {
        // Use prop organizationId first, then try to get from route params, then from deadlineEntry
        const organizationId =
          this.organizationId ||
          this.$route?.params?.organizationId ||
          deadlineEntry.organizationId;

        if (!organizationId) {
          throw new Error(
            "Fehlende organizationId oder membershipId für das Laden der Historie."
          );
        }

        const historyRef = db
          .collection("organizations")
          .doc(organizationId)
          .collection("deadlineEntries")
          .doc(deadlineEntry.meta.id)
          .collection("logs");

        const querySnapshot = await historyRef
          .orderBy("timestamp", "desc")
          .get();

        const history = [];
        querySnapshot.forEach((doc) => {
          history.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        this.historyData = history;
      } catch (error) {
        console.error("Error loading deadline entry history:", error);
        this.helpers.historyDialog.errorMessage = `Fehler beim Laden der Historie: ${error.message}`;
      } finally {
        this.helpers.historyDialog.loading = false;
      }
    },
    async toggleHistoryDialog(item) {
      if (item) {
        this.helpers.historyDialog.item = item;
        // Load history data when opening the dialog
        if (!this.helpers.historyDialog.model) {
          await this.loadHistoryData(item);
        }
      } else {
        this.helpers.historyDialog.item = null;
        this.historyData = []; // Clear history data when closing
      }
      this.helpers.historyDialog.model = !this.helpers.historyDialog.model;
    },
  },
};
</script>
